import type { SupportedLocale } from "../../lib/localeRoutes";
import { CARD_COPY } from "./catalog";
import { createCardLayout, wrapText, type CardEntry, type MeasureText } from "./layout";

export const ALLERGY_QR_PATH = "/assets/docio-allergy-doctor-qr.png";
const ALLERGY_LOGO_PATH = "/assets/docio-allergy-logo.png";
export interface CardModel {
  source: SupportedLocale; target: SupportedLocale; name: string;
  entries: readonly CardEntry[]; format: "phone" | "wallet"; emptyLabel: string;
}
interface TextBlock { lines: string[]; size: number; color: string; weight: number; height: number }
const imagePromises = new Map<string, Promise<HTMLImageElement>>();
function loadCardImage(path: string) {
  const cached = imagePromises.get(path);
  if (cached) return cached;
  const promise = new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => { imagePromises.delete(path); reject(new Error("card_asset_unavailable")); };
    image.src = path;
  });
  imagePromises.set(path, promise);
  return promise;
}

// Card branding follows the supplied full-colour Docio logo, including exports.
const palette = {
  text: "#1e293b", muted: "#536779", border: "#dce7ed", paper: "#ffffff",
  blue: "#2d6ec8", green: "#54ad6b", headerText: "#ffffff", headerMuted: "#edf4ff", tile: "#eff5fc",
};
const walletPreparation: Record<SupportedLocale, string> = {
  el: "Ελέγξτε τα συστατικά. Αποφύγετε την επαφή με αλλεργιογόνα.",
  en: "Check ingredients. Avoid cross-contact.", tr: "Malzemeleri kontrol edin. Çapraz teması önleyin.",
  de: "Zutaten prüfen. Kreuzkontakt vermeiden.", es: "Revise los ingredientes. Evite el contacto cruzado.",
  ru: "Проверьте состав. Исключите перекрёстный контакт.",
};

export async function drawAllergyCard(canvas: HTMLCanvasElement, model: CardModel, requestedWidth: number, pageIndex: number, resolution = 3) {
  const [qr, logo] = await Promise.all([loadCardImage(ALLERGY_QR_PATH), loadCardImage(ALLERGY_LOGO_PATH), document.fonts.ready]);
  const width = model.format === "wallet" ? 350 : Math.round(requestedWidth);
  const wallet = model.format === "wallet";
  const height = wallet ? width * 108 / 86 : Math.max(580, Math.min(640, width * 16 / 9));
  canvas.width = Math.round(width * resolution);
  canvas.height = Math.round(height * resolution);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas_unavailable");
  ctx.scale(resolution, resolution);
  ctx.textBaseline = "top";
  const measure: MeasureText = (text, size, weight = 400) => {
    ctx.font = `${weight} ${size}px Roboto, Arial, sans-serif`;
    return ctx.measureText(text).width;
  };
  const block = (text: string, size: number, availableWidth: number, color = palette.text, weight = 400): TextBlock => {
    const lines = wrapText(text, availableWidth, size, measure, weight);
    return { lines, size, color, weight, height: lines.length * size * 1.3 };
  };
  const paintText = (item: TextBlock, x: number, y: number, align: CanvasTextAlign = "left") => {
    ctx.font = `${item.weight} ${item.size}px Roboto, Arial, sans-serif`;
    ctx.fillStyle = item.color; ctx.textAlign = align;
    item.lines.forEach((line, index) => ctx.fillText(line, x, y + index * item.size * 1.3));
  };
  const line = (y: number, dashed = false) => {
    ctx.strokeStyle = palette.border; ctx.lineWidth = 1; ctx.setLineDash(dashed ? [4, 4] : []);
    ctx.beginPath(); ctx.moveTo(16, y); ctx.lineTo(width - 16, y); ctx.stroke(); ctx.setLineDash([]);
  };
  const primaryCopy = CARD_COPY[model.target], sourceCopy = CARD_COPY[model.source];
  const heading = block(primaryCopy.title, wallet ? 17 : 25, width - 36, palette.headerText, 700);
  const subheading = block(sourceCopy.title, wallet ? 10 : 14, width - 36, palette.headerMuted);
  const headerHeight = heading.height + subheading.height + (wallet ? 24 : 34);
  const person = block(model.name, wallet ? 11 : 13, width - 36, palette.muted, 500);
  const nameHeight = model.name ? person.height + (wallet ? 8 : 12) : 0;
  const requests = [
    block(wallet ? walletPreparation[model.target] : primaryCopy.preparation, wallet ? 11 : 12, width - 36, palette.text, 500),
    block(wallet ? walletPreparation[model.source] : sourceCopy.preparation, 11, width - 36, palette.muted),
    block(primaryCopy.unsure, wallet ? 11 : 12, width - 36, palette.text, 500),
    block(sourceCopy.unsure, 11, width - 36, palette.muted),
  ];
  const notesHeight = requests.reduce((sum, item) => sum + item.height, 0) + 22;
  const doctor = block(sourceCopy.doctor, 12, width - 126, palette.text, 500);
  const footerHeight = Math.max(110, doctor.height + 76);
  const notesTop = wallet ? height / 2 + 10 : height - footerHeight - notesHeight - 8;
  const foodsTop = headerHeight + nameHeight + (wallet ? 8 : 14);
  const foodsHeight = (wallet ? height / 2 - 14 : notesTop - 14) - foodsTop;
  if (wallet && notesTop + notesHeight > height - footerHeight + 3) throw new Error("card_content_too_long");
  const pages = createCardLayout(model.entries, width - 32, foodsHeight, measure, { singlePage: wallet });
  const safeIndex = Math.max(0, Math.min(pageIndex, pages.length - 1));

  ctx.fillStyle = palette.paper; ctx.fillRect(0, 0, width, height);
  ctx.fillStyle = palette.blue; ctx.fillRect(0, 0, width, headerHeight);
  ctx.fillStyle = palette.green; ctx.fillRect(0, headerHeight - 3, width, 3);
  paintText(heading, 18, wallet ? 10 : 15);
  paintText(subheading, 18, (wallet ? 14 : 21) + heading.height);
  if (model.name) paintText(person, 18, headerHeight + (wallet ? 6 : 12));
  const page = pages[safeIndex];
  if (page) for (const tile of page.tiles) {
    const x = 16 + tile.x, y = foodsTop + tile.y;
    ctx.fillStyle = palette.tile; ctx.beginPath(); ctx.roundRect(x, y, tile.width, tile.height, 8); ctx.fill();
    let textY = y + (tile.height - tile.textHeight) / 2;
    ctx.font = `700 ${page.fontSize}px Roboto, Arial, sans-serif`; ctx.textAlign = "center"; ctx.fillStyle = palette.text;
    tile.primaryLines.forEach(text => { ctx.fillText(text, x + tile.width / 2, textY); textY += page.fontSize * 1.2; });
    textY += page.textGap;
    ctx.font = `400 ${page.secondarySize}px Roboto, Arial, sans-serif`; ctx.fillStyle = palette.muted;
    tile.secondaryLines.forEach(text => { ctx.fillText(text, x + tile.width / 2, textY); textY += page.secondarySize * 1.25; });
  }
  else paintText(block(model.emptyLabel, 14, width - 56, palette.muted), 28, foodsTop + 24);

  line(wallet ? height / 2 : notesTop, wallet);
  let y = notesTop + (wallet ? 0 : 9);
  requests.forEach((item, index) => { paintText(item, 18, y); y += item.height + (index === 1 ? 10 : 3); });
  const footerTop = height - footerHeight;
  line(footerTop);
  // Centre the QR code and branding within the same row.
  const footerContentCenter = footerTop + 8 + (footerHeight - 30) / 2;
  const logoHeight = 40;
  const logoTextGap = 6;
  const brandTop = footerContentCenter - (logoHeight + logoTextGap + doctor.height) / 2;
  // Draw the supplied PNG directly, without rebuilding or recoloring its code.
  // The white footer supplies a quiet margin on all four sides.
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(qr, 23, footerContentCenter - 33, 66, 66);
  // Preserve the supplied logo's proportions and transparency.
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(logo, 108, brandTop, logoHeight * logo.naturalWidth / logo.naturalHeight, logoHeight);
  paintText(doctor, 108, brandTop + logoHeight + logoTextGap);
  if (pages.length > 1) paintText(block(`${safeIndex + 1}/${pages.length}`, 11, 45, palette.muted), width - 14, height - 17, "right");
  return { pages, pageIndex: safeIndex, width, height };
}
